import { raidQueueService, type QueuedStream } from '../lib/raidQueue';
import { TwitchStreamsService } from '../lib/twitchStreams';
import { twitchService } from '../lib/twitch';
import { r3LabsService, type ActiveSlot } from '../lib/r3dlabs';

const GRACE_PERIOD_MS = 10 * 60 * 1000; // 10 minutes in milliseconds

interface LastSeenSlot {
  timestamp: number;  // When r3dlabs last reported this as active
  ondeckUserId: string;
}

// Extend ActiveSlot to make ondeck fields optional since they might be in practice
interface ExtendedActiveSlot extends Omit<ActiveSlot, 'ondeck_channel_platform_user_id' | 'ondeck_channel_name'> {
  ondeck_channel_platform_user_id?: string;
  ondeck_channel_name?: string;
}

export class QueueManager {
  private static instance: QueueManager | null = null;
  private static readonly LAST_SEEN_SLOTS_KEY = 'last_seen_slots';
  private streamsService?: TwitchStreamsService;
  private checkInterval: number;
  private intervalId?: number;
  private lastSeenSlots: Map<string, LastSeenSlot>;

  private constructor() {
    // Check every 30 seconds to match r3dlabs updates
    this.checkInterval = 30 * 1000;
    this.lastSeenSlots = this.loadLastSeenSlots();
  }

  private loadLastSeenSlots(): Map<string, LastSeenSlot> {
    const stored = localStorage.getItem(QueueManager.LAST_SEEN_SLOTS_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return new Map(Object.entries(parsed));
      } catch (error) {
        console.error('Failed to load last seen slots:', error);
      }
    }
    return new Map();
  }

  private saveLastSeenSlots(): void {
    const obj = Object.fromEntries(this.lastSeenSlots);
    localStorage.setItem(QueueManager.LAST_SEEN_SLOTS_KEY, JSON.stringify(obj));
  }

  public static getInstance(): QueueManager {
    if (!QueueManager.instance) {
      QueueManager.instance = new QueueManager();
    }
    return QueueManager.instance;
  }

  private initializeIfNeeded(): boolean {
    if (this.streamsService) {
      return true;
    }

    const accessToken = twitchService.getAccessToken();
    if (!accessToken) {
      return false;
    }

    const clientId = import.meta.env.VITE_TWITCH_CLIENT_ID;
    if (!clientId) {
      return false;
    }

    this.streamsService = new TwitchStreamsService(clientId, accessToken);
    return true;
  }

  public start(): void {
    console.log('Queue manager: Starting...');
    
    // Stop any existing interval
    this.stop();

    if (!this.initializeIfNeeded()) {
      console.log('Queue manager: Cannot start without authentication');
      return;
    }

    // Do an initial check
    this.checkQueue().catch(error => {
      console.error('Queue manager: Initial check failed:', error);
    });

    // Set up periodic checks
    this.intervalId = window.setInterval(() => {
      this.checkQueue().catch(error => {
        console.error('Queue manager: Periodic check failed:', error);
        // If we get an auth error, stop the queue manager
        if (error instanceof Error && error.message.includes('auth_required')) {
          this.stop();
        }
      });
    }, this.checkInterval);

    console.log('Queue manager: Started successfully');
  }

  public stop(): void {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
    // Clear the streams service but keep last seen slots
    this.streamsService = undefined;
  }

  private shouldKeepInQueue(slotId: string): boolean {
    const lastSeen = this.lastSeenSlots.get(slotId);
    if (!lastSeen) {
      console.log(`Queue manager: No last seen data for slot ${slotId}`);
      return false;
    }
    
    // If the slot has been raided, don't keep it
    if (raidQueueService.wasSlotRaided(slotId)) {
      console.log('Queue manager: Slot was raided, removing from queue');
      return false;
    }
    
    const now = Date.now();
    const timeSinceLastSeen = now - lastSeen.timestamp;
    const withinGracePeriod = timeSinceLastSeen <= GRACE_PERIOD_MS;

    return withinGracePeriod;
  }

  private async checkQueue(): Promise<void> {
    try {
      if (!this.streamsService || !this.initializeIfNeeded()) {
        console.log('Queue manager: Cannot check queue without authentication');
        this.stop();
        return;
      }

      // Check r3dlabs active slot first
      await this.checkR3dlabsSlot();

      // Get current queue state after r3dlabs check
      const queue = raidQueueService.getQueue();
      if (queue.length === 0) {
        console.log('Queue manager: Queue is empty');
        return;
      }

      // Get current live streams
      const liveStreams = await this.streamsService.getFollowedStreams();
      const liveStreamIds = new Set(liveStreams.map(stream => stream.userId));

      // First check if we have a r3dlabs entry
      const r3dlabsEntry = queue.find(stream => stream.activeSlotId);
      
      if (r3dlabsEntry) {
        // Only update live status, don't remove/re-add the entry
        const isLive = liveStreamIds.has(r3dlabsEntry.userId);
        if (isLive !== r3dlabsEntry.isLive) {
          raidQueueService.updateStreamLiveStatus(new Set([r3dlabsEntry.userId]));
        }
      }

      // Handle non-r3dlabs entries - remove if offline
      queue.forEach(stream => {
        if (!stream.activeSlotId && !liveStreamIds.has(stream.userId)) {
          console.log(`Removing offline stream from queue: ${stream.userName}`);
          raidQueueService.removeFromQueue(stream.userId);
        }
      });

    } catch (error) {
      console.error('Error checking queue for offline streams:', error);
      // If we get an auth error, stop the queue manager
      if (error instanceof Error && error.message.includes('auth_required')) {
        this.stop();
      }
    }
  }

  private async checkR3dlabsSlot(): Promise<void> {
    try {
      if (!this.streamsService) {
        console.log('Queue manager: No streams service available');
        return;
      }

      const channelName = twitchService.getChannelName();
      if (!channelName) {
        console.log('Queue manager: No channel name available');
        return;
      }

      let eventsResponse;
      try {
        eventsResponse = await r3LabsService.getUserEvents(channelName);
      } catch (error) {
        console.error('Queue manager: Failed to fetch r3dlabs events:', error);
        return;
      }

      if (!eventsResponse) {
        console.log('Queue manager: No r3dlabs response received');
        return;
      }

      // Cast activeSlot to ExtendedActiveSlot to handle optional ondeck fields
      const activeSlot = eventsResponse.active_slot as ExtendedActiveSlot;
      const queue = raidQueueService.getQueue();
      const existingEntry = queue.find(stream => stream.activeSlotId);

      // Log current state
      console.log('Queue manager: Current state:', {
        hasActiveSlot: !!activeSlot,
        activeSlotId: activeSlot?.active_slot_id,
        existingEntrySlotId: existingEntry?.activeSlotId,
        queueLength: queue.length,
        lastSeenSlotsCount: this.lastSeenSlots.size
      });

      // If we have an active slot, always update its last seen time
      if (activeSlot?.active_slot_id && activeSlot.ondeck_channel_platform_user_id) {
        const lastSeen: LastSeenSlot = {
          timestamp: Date.now(),
          ondeckUserId: activeSlot.ondeck_channel_platform_user_id
        };
        this.lastSeenSlots.set(activeSlot.active_slot_id, lastSeen);
        this.saveLastSeenSlots();
        console.log(`Queue manager: Updated last seen time for slot ${activeSlot.active_slot_id}`);
      }

      // First, check if we have an existing entry that's no longer the active slot
      if (existingEntry?.activeSlotId && (!activeSlot || activeSlot.active_slot_id !== existingEntry.activeSlotId)) {
        console.log('Queue manager: Existing entry no longer reported by r3dlabs');
        
        // Check if we should keep it
        if (this.shouldKeepInQueue(existingEntry.activeSlotId)) {
          console.log('Queue manager: Within grace period, keeping entry');
          return;
        } else {
          console.log('Queue manager: Grace period expired or slot was raided, removing entry');
          raidQueueService.removeFromQueue(existingEntry.userId);
          // Clean up last seen data
          if (existingEntry.activeSlotId) {
            this.lastSeenSlots.delete(existingEntry.activeSlotId);
          }
          this.saveLastSeenSlots();
          return;
        }
      }

      // Then handle active slot if we have one
      if (activeSlot) {
        console.log('Queue manager: Processing active slot');

        // Check if we have all required fields
        if (!activeSlot.ondeck_channel_platform_user_id || !activeSlot.ondeck_channel_name) {
          console.log('Queue manager: Missing required ondeck channel information');
          if (existingEntry) {
            console.log('Queue manager: Removing existing entry due to missing ondeck info');
            raidQueueService.removeFromQueue(existingEntry.userId);
            if (existingEntry.activeSlotId) {
              this.lastSeenSlots.delete(existingEntry.activeSlotId);
            }
            this.saveLastSeenSlots();
          }
          return;
        }

        // Check if the ondeck user has changed
        const ondeckUserChanged = existingEntry && 
          existingEntry.userId !== activeSlot.ondeck_channel_platform_user_id;

        if (ondeckUserChanged) {
          console.log('Queue manager: Ondeck user changed, updating queue');
          // Remove the old entry
          raidQueueService.removeFromQueue(existingEntry.userId);
          // Don't return here, let it continue to add the new entry
        } else if (existingEntry && activeSlot.active_slot_id === existingEntry.activeSlotId) {
          // Same slot and same ondeck user, just ensure it's at position 0
          if (existingEntry.queuePosition !== 0) {
            raidQueueService.moveToPosition(existingEntry.userId, 0);
          }
          return;
        }

        // Add new entry - always add regardless of live status
        const userData = await this.streamsService.getUserById(activeSlot.ondeck_channel_platform_user_id);
        
        // Check live status but don't let it prevent adding to queue
        const liveStreams = await this.streamsService.getFollowedStreams();
        const isLive = liveStreams.some(stream => 
          stream.userId === activeSlot.ondeck_channel_platform_user_id
        );

        console.log(`Queue manager: Adding new r3dlabs entry to queue: ${activeSlot.ondeck_channel_name} (${isLive ? 'online' : 'offline'})`);
        
        const newStream: Omit<QueuedStream, 'queuePosition'> = {
          userId: activeSlot.ondeck_channel_platform_user_id,
          userLogin: activeSlot.ondeck_channel_name.toLowerCase(),
          userName: activeSlot.ondeck_channel_name,
          profileImageUrl: userData?.profile_image_url || '',
          title: `Next up in ${activeSlot.event_name}`,
          startedAt: new Date().toISOString(),
          viewerCount: 0,
          thumbnailUrl: '',
          activeSlotId: activeSlot.active_slot_id,
          eventName: activeSlot.event_name,
          isLive,
          wasRaided: false
        };

        // Add to queue and ensure it's at position 0
        raidQueueService.addToQueue(newStream);
        raidQueueService.moveToPosition(newStream.userId, 0);
      }
    } catch (error) {
      console.error('Error checking r3dlabs slot:', error);
    }
  }
}

// Export singleton instance getter
export const queueManager = QueueManager.getInstance();
